import React, { useState, useEffect } from 'react';
import { COLORS, TIER_NAMES, TIER_COLORS } from '../data/mockData';
import { useAuth } from '../context/AuthContext';
import {
  Users, ShoppingCart, BarChart3, Trash2, Edit3, Shield, ShieldCheck,
  Package, TrendingUp, ClipboardList, ChevronLeft, ChevronRight,
  Crown, AlertCircle, X, BookOpen, Plus, Upload, FileText, Clock, Save, File, CheckCircle2, Loader2, Send, Mail, Eye, Search, Globe, MessageSquare, Link2, PlugZap, FileCode, GraduationCap
} from 'lucide-react';
import {
  fetchAdminStats, fetchAdminUsers, updateAdminUser, deleteAdminUser,
  fetchAdminOrders, fetchAdminProducts, addAdminProduct, deleteAdminProduct,
  fetchAdminResearch, createAdminResearch, updateAdminResearch, deleteAdminResearch, importAdminResearch, uploadAdminResearch, scrapeAdminResearch,
  sendContentBlast, fetchContentBlasts, fetchBlastSchedule, updateBlastSchedule, testAutoBlast
} from '../services/api';
import AdminMedicalPage from './AdminMedicalPage';
import Modal from './admin/AdminModal';
import GroupChatReportTab from './admin/GroupChatReportTab';
import AffiliatesTab from './admin/AffiliatesTab';
import IntegrationsTab from './admin/IntegrationsTab';
import DocumentationTab from './admin/DocumentationTab';
import TrainingTab from './admin/TrainingTab';

const TABS = [
  { key: 'overview', label: 'Overview', icon: BarChart3 },
  { key: 'users', label: 'Users', icon: Users },
  { key: 'research', label: 'Research', icon: BookOpen },
  { key: 'email', label: 'Content Blasts', icon: Mail },
  { key: 'orders', label: 'Orders', icon: ShoppingCart },
  { key: 'products', label: 'Products', icon: Package },
  { key: 'affiliates', label: 'Affiliates', icon: Link2 },
  { key: 'integrations', label: 'Integrations', icon: PlugZap },
  { key: 'architecture', label: 'Architecture', icon: FileCode },
  { key: 'training', label: 'Training', icon: GraduationCap },
  { key: 'medical', label: 'Medical Vault', icon: ShieldCheck },
  { key: 'groupchat', label: 'Group Chat', icon: MessageSquare },
];

const RESEARCH_CATEGORIES = [
  'sleep', 'nutrition', 'exercise', 'social', 'mental_wellness', 'supplements', 'general',
];

const inputStyle = { width: '100%', padding: '10px 14px', borderRadius: 10, border: '1px solid #E2DAC8', fontSize: 14, color: '#2F3E46', backgroundColor: '#FFF' };
const labelStyle = { fontSize: 13, fontWeight: 600, color: '#5C6B73', display: 'block', marginBottom: 4 };

export default function AdminPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [usersTotal, setUsersTotal] = useState(0);
  const [usersPage, setUsersPage] = useState(1);
  const [orders, setOrders] = useState([]);
  const [ordersTotal, setOrdersTotal] = useState(0);
  const [ordersPage, setOrdersPage] = useState(1);
  const [products, setProducts] = useState({ default_products: [], custom_products: [] });
  const [editUser, setEditUser] = useState(null);
  const [showAddProduct, setShowAddProduct] = useState(false);
  const [newProduct, setNewProduct] = useState({ name: '', description: '', price: '', category: 'health-devices', icon: 'Package', featured: false });

  // Research state
  const [articles, setArticles] = useState([]);
  const [showAddArticle, setShowAddArticle] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [editArticle, setEditArticle] = useState(null);
  const [viewArticle, setViewArticle] = useState(null);
  const [newArticle, setNewArticle] = useState({ title: '', summary: '', content: '', category: 'general', source: '', read_time: '5 min', tier_required: 0 });
  const [importData, setImportData] = useState({ text: '', category: 'general', source: '', tier_required: 0 });

  // Scrape state
  const [showScrape, setShowScrape] = useState(false);
  const [scrapeUrl, setScrapeUrl] = useState('');
  const [scrapeMeta, setScrapeMeta] = useState({ category: 'general', tier_required: 0 });
  const [scraping, setScraping] = useState(false);
  const [scrapeResult, setScrapeResult] = useState(null);

  // File upload state
  const [showUpload, setShowUpload] = useState(false);
  const [uploadFiles, setUploadFiles] = useState([]);
  const [uploadMeta, setUploadMeta] = useState({ category: 'general', source: '', tier_required: 0 });
  const [uploading, setUploading] = useState(false);
  const [uploadResults, setUploadResults] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Content blast state
  const [blasts, setBlasts] = useState([]);
  const [showBlastForm, setShowBlastForm] = useState(false);
  const [blastForm, setBlastForm] = useState({ subject: '', custom_message: '', min_tier: 2, include_article_links: true });
  const [blasting, setBlasting] = useState(false);
  const [blastResult, setBlastResult] = useState(null);

  // Schedule state
  const [schedule, setSchedule] = useState(null);
  const [scheduleForm, setScheduleForm] = useState({ enabled: false, days: ['mon', 'wed', 'fri'], hour: 9, minute: 0, min_tier: 2, include_article_links: true });
  const [savingSchedule, setSavingSchedule] = useState(false);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    if (user?.role !== 'admin') return;
    loadStats();
  }, [user]);

  useEffect(() => {
    if (user?.role !== 'admin') return;
    if (tab === 'users') loadUsers(usersPage);
    if (tab === 'orders') loadOrders(ordersPage);
    if (tab === 'products') loadProducts();
    if (tab === 'research') loadResearch();
    if (tab === 'email') loadBlasts();
  }, [tab, usersPage, ordersPage, user]);

  const loadStats = async () => { try { setStats(await fetchAdminStats()); } catch (e) { console.error(e); } };
  const loadUsers = async (page) => { try { const d = await fetchAdminUsers(page); setUsers(d.users); setUsersTotal(d.total); } catch (e) { console.error(e); } };
  const loadOrders = async (page) => { try { const d = await fetchAdminOrders(page); setOrders(d.orders); setOrdersTotal(d.total); } catch (e) { console.error(e); } };
  const loadProducts = async () => { try { setProducts(await fetchAdminProducts()); } catch (e) { console.error(e); } };
  const loadResearch = async () => { try { setArticles(await fetchAdminResearch()); } catch (e) { console.error(e); } };
  const loadBlasts = async () => {
    try { setBlasts(await fetchContentBlasts()); } catch (e) { console.error(e); }
    try { const s = await fetchBlastSchedule(); setSchedule(s); setScheduleForm({ enabled: s.enabled, days: s.days || ['mon','wed','fri'], hour: s.hour || 9, minute: s.minute || 0, min_tier: s.min_tier || 2, include_article_links: s.include_article_links !== false }); } catch (e) { console.error(e); }
  };

  const handleUpdateUser = async () => {
    if (!editUser) return;
    try { await updateAdminUser(editUser.user_id, { role: editUser.role, membership_tier: editUser.membership_tier, name: editUser.name }); setEditUser(null); loadUsers(usersPage); loadStats(); }
    catch (e) { alert(e.response?.data?.detail || 'Failed'); }
  };
  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Delete this user and all their data?')) return;
    try { await deleteAdminUser(userId); loadUsers(usersPage); loadStats(); } catch (e) { alert(e.response?.data?.detail || 'Failed'); }
  };
  const handleAddProduct = async () => {
    if (!newProduct.name || !newProduct.price) return;
    try { await addAdminProduct({ ...newProduct, price: parseFloat(newProduct.price) }); setNewProduct({ name: '', description: '', price: '', category: 'health-devices', icon: 'Package', featured: false }); setShowAddProduct(false); loadProducts(); }
    catch (e) { alert('Failed to add product'); }
  };
  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('Delete this custom product?')) return;
    try { await deleteAdminProduct(productId); loadProducts(); } catch (e) { alert('Failed'); }
  };

  // Research handlers
  const handleAddArticle = async () => {
    if (!newArticle.title.trim()) { alert('Title is required'); return; }
    if (!newArticle.summary.trim() && !newArticle.content.trim()) { alert('Please add a summary or content'); return; }
    try {
      await createAdminResearch(newArticle);
      setNewArticle({ title: '', summary: '', content: '', category: 'general', source: '', read_time: '5 min', tier_required: 0 });
      setShowAddArticle(false);
      loadResearch();
    } catch (e) { alert(e.response?.data?.detail || 'Failed to create article'); }
  };

  const handleUpdateArticle = async () => {
    if (!editArticle) return;
    try {
      const { article_id, created_at, ...fields } = editArticle;
      await updateAdminResearch(article_id, fields);
      setEditArticle(null);
      loadResearch();
    } catch (e) { alert(e.response?.data?.detail || 'Failed to update article'); }
  };

  const handleDeleteArticle = async (articleId) => {
    if (!window.confirm('Delete this research article?')) return;
    try { await deleteAdminResearch(articleId); loadResearch(); } catch (e) { alert('Failed'); }
  };

  const handleImport = async () => {
    if (!importData.text.trim()) return;
    try {
      await importAdminResearch(importData);
      setImportData({ text: '', category: 'general', source: '', tier_required: 0 });
      setShowImport(false);
      loadResearch();
    } catch (e) { alert(e.response?.data?.detail || 'Failed to import'); }
  };

  const handleScrape = async () => {
    if (!scrapeUrl.trim()) return;
    setScraping(true);
    setScrapeResult(null);
    try {
      const result = await scrapeAdminResearch({ url: scrapeUrl, category: scrapeMeta.category, tier_required: scrapeMeta.tier_required });
      setScrapeResult(result);
      loadResearch();
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to scrape URL');
    }
    setScraping(false);
  };

  const handleFileUpload = async () => {
    if (uploadFiles.length === 0) return;
    setUploading(true);
    setUploadResults([]);
    setUploadProgress(0);
    const results = [];
    for (let i = 0; i < uploadFiles.length; i++) {
      setUploadProgress(i + 1);
      const file = uploadFiles[i];
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('category', uploadMeta.category);
        formData.append('source', uploadMeta.source);
        formData.append('tier_required', uploadMeta.tier_required.toString());
        const result = await uploadAdminResearch(formData);
        results.push({ file: file.name, status: 'success', data: result });
      } catch (e) {
        results.push({ file: file.name, status: 'error', error: e.response?.data?.detail || 'Upload failed' });
      }
    }
    setUploadResults(results);
    setUploadFiles([]);
    setUploading(false);
    loadResearch();
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length) {
      setUploadFiles(prev => [...prev, ...files]);
      setUploadResults([]);
    }
    e.target.value = '';
  };

  const removeFile = (idx) => {
    setUploadFiles(prev => prev.filter((_, i) => i !== idx));
  };

  const resetUploadModal = () => {
    setShowUpload(false);
    setUploadFiles([]);
    setUploadMeta({ category: 'general', source: '', tier_required: 0 });
    setUploadResults([]);
    setUploadProgress(0);
  };

  const handleSendBlast = async () => {
    if (!window.confirm(`Send content emails to all Tier ${blastForm.min_tier}+ users?`)) return;
    setBlasting(true);
    setBlastResult(null);
    try {
      const result = await sendContentBlast(blastForm);
      setBlastResult(result);
      setShowBlastForm(false);
      loadBlasts();
    } catch (e) { alert(e.response?.data?.detail || 'Failed to send'); }
    setBlasting(false);
  };

  const handleSaveSchedule = async () => {
    setSavingSchedule(true);
    try {
      const result = await updateBlastSchedule(scheduleForm);
      setSchedule(result);
    } catch (e) { alert(e.response?.data?.detail || 'Failed to save schedule'); }
    setSavingSchedule(false);
  };

  const handleTestBlast = async () => {
    if (!window.confirm('Send a test auto-blast right now?')) return;
    setTesting(true);
    try { await testAutoBlast(); loadBlasts(); } catch (e) { alert('Failed'); }
    setTesting(false);
  };

  const toggleDay = (day) => {
    setScheduleForm(prev => ({
      ...prev,
      days: prev.days.includes(day) ? prev.days.filter(d => d !== day) : [...prev.days, day],
    }));
  };

  if (user?.role !== 'admin') {
    return (
      <div style={{ padding: 40, textAlign: 'center' }}>
        <AlertCircle size={48} color={COLORS.error} />
        <h2 style={{ color: COLORS.textPrimary, fontFamily: 'Outfit, sans-serif', marginTop: 16 }}>Access Denied</h2>
        <p style={{ color: COLORS.textSecondary }}>You need administrator privileges to access this page.</p>
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard" style={{ padding: '24px 20px', maxWidth: 1200, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
        <Shield size={28} color={COLORS.primary} />
        <h1 style={{ fontSize: 28, fontWeight: 800, color: COLORS.textPrimary, fontFamily: 'Outfit, sans-serif' }}>Admin Panel</h1>
      </div>

      {/* Tab Navigation */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 24, flexWrap: 'wrap' }}>
        {TABS.map(t => {
          const Icon = t.icon;
          const active = tab === t.key;
          return (
            <button key={t.key} data-testid={`admin-tab-${t.key}`} onClick={() => setTab(t.key)}
              style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 18px', borderRadius: 10, border: 'none', cursor: 'pointer', fontSize: 14, fontWeight: active ? 700 : 500, backgroundColor: active ? COLORS.primary : COLORS.surface, color: active ? '#FFF' : COLORS.textSecondary, transition: 'all 0.2s' }}>
              <Icon size={18} /> {t.label}
            </button>
          );
        })}
      </div>

      {/* Overview Tab */}
      {tab === 'overview' && stats && (
        <div data-testid="admin-overview">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 16 }}>
            {[
              { label: 'Total Users', value: stats.total_users, icon: Users, color: COLORS.secondary },
              { label: 'Recent Signups (7d)', value: stats.recent_signups, icon: TrendingUp, color: COLORS.success },
              { label: 'Total Orders', value: stats.total_orders, icon: ShoppingCart, color: COLORS.tierOptimizer },
              { label: 'Revenue', value: `$${stats.total_revenue.toLocaleString()}`, icon: BarChart3, color: COLORS.tierElite },
              { label: 'Active Goals', value: stats.total_goals, icon: ClipboardList, color: COLORS.primary },
              { label: 'Chat Messages', value: stats.total_messages, icon: Crown, color: COLORS.accentWarm },
              { label: 'Survey Rate', value: `${stats.survey_completion_rate}%`, icon: ClipboardList, color: COLORS.success },
              { label: 'Admin Users', value: stats.admin_users, icon: Shield, color: COLORS.error },
            ].map((s, i) => {
              const Icon = s.icon;
              return (
                <div key={i} style={{ backgroundColor: COLORS.surface, borderRadius: 16, padding: 20, boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                    <div style={{ width: 40, height: 40, borderRadius: 10, backgroundColor: `${s.color}18`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Icon size={20} color={s.color} />
                    </div>
                  </div>
                  <div style={{ fontSize: 26, fontWeight: 800, color: COLORS.textPrimary, fontFamily: 'Outfit, sans-serif' }}>{s.value}</div>
                  <div style={{ fontSize: 13, color: COLORS.textSecondary, marginTop: 2 }}>{s.label}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Users Tab */}
      {tab === 'users' && (
        <div data-testid="admin-users">
          <div style={{ backgroundColor: COLORS.surface, borderRadius: 16, overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                <thead>
                  <tr style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                    {['Name', 'Email', 'Role', 'Tier', 'Survey', 'Joined', 'Actions'].map(h => (
                      <th key={h} style={{ padding: '14px 16px', textAlign: 'left', fontWeight: 600, color: COLORS.textSecondary, fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {users.map(u => (
                    <tr key={u.user_id} style={{ borderBottom: `1px solid ${COLORS.border}20` }}>
                      <td style={{ padding: '12px 16px', fontWeight: 600, color: COLORS.textPrimary }}>{u.name}</td>
                      <td style={{ padding: '12px 16px', color: COLORS.textSecondary }}>{u.email}</td>
                      <td style={{ padding: '12px 16px' }}><span style={{ padding: '4px 10px', borderRadius: 9999, fontSize: 12, fontWeight: 700, backgroundColor: u.role === 'admin' ? `${COLORS.error}18` : `${COLORS.primary}18`, color: u.role === 'admin' ? COLORS.error : COLORS.primary }}>{u.role}</span></td>
                      <td style={{ padding: '12px 16px' }}><span style={{ padding: '4px 10px', borderRadius: 9999, fontSize: 12, fontWeight: 700, backgroundColor: `${TIER_COLORS[u.membership_tier] || '#999'}18`, color: TIER_COLORS[u.membership_tier] || '#999' }}>{TIER_NAMES[u.membership_tier] || 'Free'}</span></td>
                      <td style={{ padding: '12px 16px', color: u.survey_completed ? COLORS.success : COLORS.textSecondary }}>{u.survey_completed ? 'Done' : 'Pending'}</td>
                      <td style={{ padding: '12px 16px', color: COLORS.textSecondary, fontSize: 13 }}>{u.created_at ? new Date(u.created_at).toLocaleDateString() : '-'}</td>
                      <td style={{ padding: '12px 16px' }}>
                        <div style={{ display: 'flex', gap: 8 }}>
                          <button data-testid={`edit-user-${u.user_id}`} onClick={() => setEditUser({ ...u })} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}><Edit3 size={16} color={COLORS.secondary} /></button>
                          <button data-testid={`delete-user-${u.user_id}`} onClick={() => handleDeleteUser(u.user_id)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}><Trash2 size={16} color={COLORS.error} /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderTop: `1px solid ${COLORS.border}` }}>
              <span style={{ fontSize: 13, color: COLORS.textSecondary }}>Showing {users.length} of {usersTotal}</span>
              <div style={{ display: 'flex', gap: 8 }}>
                <button disabled={usersPage <= 1} onClick={() => setUsersPage(p => p - 1)} style={{ padding: '6px 12px', borderRadius: 8, border: `1px solid ${COLORS.border}`, backgroundColor: COLORS.surface, cursor: usersPage <= 1 ? 'not-allowed' : 'pointer', opacity: usersPage <= 1 ? 0.5 : 1 }}><ChevronLeft size={16} /></button>
                <button disabled={usersPage * 50 >= usersTotal} onClick={() => setUsersPage(p => p + 1)} style={{ padding: '6px 12px', borderRadius: 8, border: `1px solid ${COLORS.border}`, backgroundColor: COLORS.surface, cursor: usersPage * 50 >= usersTotal ? 'not-allowed' : 'pointer', opacity: usersPage * 50 >= usersTotal ? 0.5 : 1 }}><ChevronRight size={16} /></button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Research Tab */}
      {tab === 'research' && (
        <div data-testid="admin-research">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
            <h3 style={{ fontSize: 18, fontWeight: 700, color: COLORS.textPrimary, fontFamily: 'Outfit, sans-serif' }}>Research Articles ({articles.length})</h3>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <button data-testid="scrape-web-btn" onClick={() => { setShowScrape(true); setScrapeResult(null); }}
                style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '10px 18px', borderRadius: 10, border: `1px solid ${COLORS.secondary}`, backgroundColor: 'transparent', color: COLORS.secondary, fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>
                <Search size={16} /> Scrape Web
              </button>
              <button data-testid="upload-file-btn" onClick={() => setShowUpload(true)}
                style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '10px 18px', borderRadius: 10, border: `1px solid ${COLORS.success}`, backgroundColor: 'transparent', color: COLORS.success, fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>
                <File size={16} /> Upload File
              </button>
              <button data-testid="import-research-btn" onClick={() => setShowImport(true)}
                style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '10px 18px', borderRadius: 10, border: `1px solid ${COLORS.primary}`, backgroundColor: 'transparent', color: COLORS.primary, fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>
                <Upload size={16} /> Import Text
              </button>
              <button data-testid="add-research-btn" onClick={() => setShowAddArticle(true)}
                style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '10px 18px', borderRadius: 10, border: 'none', backgroundColor: COLORS.primary, color: '#FFF', fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>
                <Plus size={16} /> New Article
              </button>
            </div>
          </div>

          {/* Articles list */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {articles.filter(a => a.title).map(a => (
              <div key={a.article_id} data-testid={`admin-article-${a.article_id}`}
                style={{ backgroundColor: COLORS.surface, borderRadius: 14, padding: 18, boxShadow: '0 1px 4px rgba(0,0,0,0.04)', display: 'flex', gap: 16, alignItems: 'flex-start' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
                    <span style={{ padding: '3px 10px', borderRadius: 6, fontSize: 11, fontWeight: 700, backgroundColor: `${COLORS.primary}15`, color: COLORS.primary, textTransform: 'capitalize' }}>{a.category}</span>
                    <span style={{ fontSize: 12, color: COLORS.textSecondary, display: 'flex', alignItems: 'center', gap: 3 }}><Clock size={12} /> {a.read_time}</span>
                    {a.source && <span style={{ fontSize: 12, color: COLORS.textSecondary }}>{a.source}</span>}
                    <span style={{ fontSize: 12, color: COLORS.textSecondary }}>{a.published}</span>
                    {a.tier_required > 0 && <span style={{ padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 700, backgroundColor: `${TIER_COLORS[a.tier_required]}18`, color: TIER_COLORS[a.tier_required] }}>{TIER_NAMES[a.tier_required]}+</span>}
                  </div>
                  <h4 style={{ fontSize: 16, fontWeight: 700, color: COLORS.textPrimary, fontFamily: 'Outfit, sans-serif', marginBottom: 4 }}>{a.title}</h4>
                  <p style={{ fontSize: 13, color: COLORS.textSecondary, lineHeight: 1.5, margin: 0 }}>{a.summary}</p>
                  {a.content && <p style={{ fontSize: 12, color: COLORS.border, marginTop: 4 }}>{a.content.length} chars of full content</p>}
                </div>
                <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                  <button data-testid={`view-article-${a.article_id}`} onClick={() => setViewArticle(a)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 6 }}><Eye size={16} color={COLORS.primary} /></button>
                  <button data-testid={`edit-article-${a.article_id}`} onClick={() => setEditArticle({ ...a })} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 6 }}><Edit3 size={16} color={COLORS.secondary} /></button>
                  <button data-testid={`delete-article-${a.article_id}`} onClick={() => handleDeleteArticle(a.article_id)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 6 }}><Trash2 size={16} color={COLORS.error} /></button>
                </div>
              </div>
            ))}
            {articles.length === 0 && (
              <div style={{ textAlign: 'center', padding: 40, color: COLORS.textSecondary }}>No research articles yet. Add one or import from text.</div>
            )}
          </div>
        </div>
      )}

      {/* Content Blasts Tab */}
      {tab === 'email' && (
        <div data-testid="admin-email">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 10 }}>
            <div>
              <h3 style={{ fontSize: 18, fontWeight: 700, color: COLORS.textPrimary, fontFamily: 'Outfit, sans-serif' }}>Content Blasts</h3>
              <p style={{ fontSize: 13, color: COLORS.textSecondary, marginTop: 4 }}>Automated and manual health insight emails for Tier 2+ users.</p>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button data-testid="test-blast-btn" onClick={handleTestBlast} disabled={testing}
                style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '10px 18px', borderRadius: 10, border: `1px solid ${COLORS.secondary}`, backgroundColor: 'transparent', color: COLORS.secondary, fontWeight: 600, fontSize: 13, cursor: 'pointer', opacity: testing ? 0.7 : 1 }}>
                {testing ? 'Sending...' : 'Test Now'}
              </button>
              <button data-testid="new-blast-btn" onClick={() => { setShowBlastForm(true); setBlastResult(null); }}
                style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 18px', borderRadius: 10, border: 'none', backgroundColor: COLORS.primary, color: '#FFF', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
                <Send size={14} /> Manual Blast
              </button>
            </div>
          </div>

          {/* Schedule Configuration */}
          <div data-testid="schedule-config" style={{ backgroundColor: COLORS.surface, borderRadius: 16, padding: 22, marginBottom: 20, boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <Clock size={20} color={COLORS.primary} />
                <h4 style={{ fontSize: 16, fontWeight: 700, color: COLORS.textPrimary, fontFamily: 'Outfit, sans-serif', margin: 0 }}>Auto-Blast Schedule</h4>
              </div>
              <button data-testid="toggle-schedule" onClick={() => setScheduleForm(p => ({ ...p, enabled: !p.enabled }))}
                style={{ width: 52, height: 28, borderRadius: 14, border: 'none', cursor: 'pointer', backgroundColor: scheduleForm.enabled ? COLORS.success : COLORS.border, position: 'relative', transition: 'background-color 0.3s' }}>
                <div style={{ width: 22, height: 22, borderRadius: 11, backgroundColor: '#FFF', position: 'absolute', top: 3, left: scheduleForm.enabled ? 27 : 3, transition: 'left 0.3s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
              </button>
            </div>

            {scheduleForm.enabled && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {/* Days */}
                <div>
                  <label style={labelStyle}>Send on these days</label>
                  <div style={{ display: 'flex', gap: 6 }}>
                    {[{k:'mon',l:'Mon'},{k:'tue',l:'Tue'},{k:'wed',l:'Wed'},{k:'thu',l:'Thu'},{k:'fri',l:'Fri'},{k:'sat',l:'Sat'},{k:'sun',l:'Sun'}].map(d => (
                      <button key={d.k} data-testid={`day-${d.k}`} onClick={() => toggleDay(d.k)}
                        style={{ width: 44, height: 38, borderRadius: 8, border: `2px solid ${scheduleForm.days.includes(d.k) ? COLORS.primary : COLORS.border}`, backgroundColor: scheduleForm.days.includes(d.k) ? `${COLORS.primary}12` : 'transparent', color: scheduleForm.days.includes(d.k) ? COLORS.primary : COLORS.textSecondary, fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>
                        {d.l}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Time & Tier */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                  <div>
                    <label style={labelStyle}>Time (UTC)</label>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <select value={scheduleForm.hour} onChange={e => setScheduleForm(p => ({ ...p, hour: parseInt(e.target.value) }))} style={{ ...inputStyle, flex: 1 }}>
                        {Array.from({length: 24}, (_, i) => <option key={i} value={i}>{String(i).padStart(2, '0')}:00</option>)}
                      </select>
                    </div>
                  </div>
                  <div>
                    <label style={labelStyle}>Min Tier</label>
                    <select value={scheduleForm.min_tier} onChange={e => setScheduleForm(p => ({ ...p, min_tier: parseInt(e.target.value) }))} style={inputStyle}>
                      {TIER_NAMES.map((name, i) => i >= 2 ? <option key={i} value={i}>{name}</option> : null)}
                    </select>
                  </div>
                  <div>
                    <label style={labelStyle}>Article Links</label>
                    <select value={scheduleForm.include_article_links ? 'yes' : 'no'} onChange={e => setScheduleForm(p => ({ ...p, include_article_links: e.target.value === 'yes' }))} style={inputStyle}>
                      <option value="yes">Tier 3+ gets links</option>
                      <option value="no">No links</option>
                    </select>
                  </div>
                </div>

                {/* Save */}
                <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                  <button data-testid="save-schedule-btn" onClick={handleSaveSchedule} disabled={savingSchedule}
                    style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '10px 20px', borderRadius: 10, border: 'none', backgroundColor: COLORS.primary, color: '#FFF', fontWeight: 700, fontSize: 14, cursor: 'pointer', opacity: savingSchedule ? 0.7 : 1 }}>
                    <Save size={16} /> {savingSchedule ? 'Saving...' : 'Save Schedule'}
                  </button>
                  {schedule?.next_run && <span style={{ fontSize: 12, color: COLORS.textSecondary }}>Next run: {new Date(schedule.next_run).toLocaleString()}</span>}
                </div>
              </div>
            )}

            {!scheduleForm.enabled && (
              <p style={{ fontSize: 13, color: COLORS.textSecondary, margin: 0 }}>Toggle on to enable automated content blasts. Emails will be sent on your chosen days at the scheduled time.</p>
            )}
          </div>

          {/* Tier explanation */}
          <div style={{ backgroundColor: COLORS.surfaceSecondary, borderRadius: 14, padding: 18, marginBottom: 20 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 10 }}>
              {[
                { tier: 'Tier 2 (Optimizer)', desc: 'Health tips & teasers', color: TIER_COLORS[2] },
                { tier: 'Tier 3 (Elite)', desc: 'Full content + article links', color: TIER_COLORS[3] },
              ].map((item, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', backgroundColor: '#FFF', borderRadius: 10 }}>
                  <div style={{ width: 8, height: 32, borderRadius: 4, backgroundColor: item.color }} />
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.textPrimary }}>{item.tier}</div>
                    <div style={{ fontSize: 12, color: COLORS.textSecondary }}>{item.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Last blast result */}
          {blastResult && (
            <div data-testid="blast-result" style={{ backgroundColor: '#E8F5E9', borderRadius: 14, padding: 18, marginBottom: 20, display: 'flex', alignItems: 'center', gap: 12 }}>
              <CheckCircle2 size={24} color={COLORS.success} />
              <div>
                <div style={{ fontSize: 15, fontWeight: 700, color: COLORS.textPrimary }}>Blast Sent Successfully</div>
                <div style={{ fontSize: 13, color: COLORS.textSecondary }}>{blastResult.sent} of {blastResult.total} emails delivered · {blastResult.failed} failed</div>
              </div>
            </div>
          )}

          {/* Blast history */}
          <h4 style={{ fontSize: 14, fontWeight: 600, color: COLORS.textSecondary, marginBottom: 12, textTransform: 'uppercase', letterSpacing: 0.5 }}>Blast History</h4>
          {blasts.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, backgroundColor: COLORS.surface, borderRadius: 14 }}>
              <Mail size={40} color={COLORS.border} />
              <p style={{ color: COLORS.textSecondary, marginTop: 12 }}>No blasts sent yet.</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {blasts.map((b, i) => (
                <div key={i} style={{ backgroundColor: COLORS.surface, borderRadius: 12, padding: 16, display: 'flex', alignItems: 'center', gap: 14 }}>
                  <div style={{ width: 40, height: 40, borderRadius: 10, backgroundColor: b.auto ? `${COLORS.secondary}12` : `${COLORS.primary}12`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    {b.auto ? <Clock size={20} color={COLORS.secondary} /> : <Mail size={20} color={COLORS.primary} />}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ fontSize: 14, fontWeight: 700, color: COLORS.textPrimary }}>{b.subject}</span>
                      {b.auto && <span style={{ padding: '2px 6px', borderRadius: 4, fontSize: 10, fontWeight: 700, backgroundColor: `${COLORS.secondary}15`, color: COLORS.secondary }}>AUTO</span>}
                    </div>
                    <div style={{ fontSize: 12, color: COLORS.textSecondary, marginTop: 2 }}>Tier {b.min_tier}+ · {b.total_sent} sent · {b.total_failed} failed · {new Date(b.created_at).toLocaleString()}</div>
                  </div>
                  <span style={{ padding: '4px 10px', borderRadius: 9999, fontSize: 12, fontWeight: 600, backgroundColor: b.total_failed === 0 ? `${COLORS.success}18` : `${COLORS.accentWarm}18`, color: b.total_failed === 0 ? COLORS.success : COLORS.accentWarm }}>
                    {b.total_recipients} recipients
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Orders Tab */}
      {tab === 'orders' && (
        <div data-testid="admin-orders">
          <div style={{ backgroundColor: COLORS.surface, borderRadius: 16, overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                <thead>
                  <tr style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                    {['Order ID', 'User', 'Items', 'Total', 'Status', 'Date'].map(h => (
                      <th key={h} style={{ padding: '14px 16px', textAlign: 'left', fontWeight: 600, color: COLORS.textSecondary, fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {orders.map(o => (
                    <tr key={o.order_id} style={{ borderBottom: `1px solid ${COLORS.border}20` }}>
                      <td style={{ padding: '12px 16px', fontWeight: 600, color: COLORS.textPrimary, fontFamily: 'monospace', fontSize: 12 }}>{o.order_id}</td>
                      <td style={{ padding: '12px 16px', color: COLORS.textSecondary }}>{o.user_id}</td>
                      <td style={{ padding: '12px 16px', color: COLORS.textSecondary }}>{o.items?.length || 0} items</td>
                      <td style={{ padding: '12px 16px', fontWeight: 700, color: COLORS.primary }}>${o.total?.toFixed(2)}</td>
                      <td style={{ padding: '12px 16px' }}><span style={{ padding: '4px 10px', borderRadius: 9999, fontSize: 12, fontWeight: 700, backgroundColor: `${COLORS.success}18`, color: COLORS.success }}>{o.status}</span></td>
                      <td style={{ padding: '12px 16px', color: COLORS.textSecondary, fontSize: 13 }}>{o.created_at ? new Date(o.created_at).toLocaleDateString() : '-'}</td>
                    </tr>
                  ))}
                  {orders.length === 0 && <tr><td colSpan={6} style={{ padding: 32, textAlign: 'center', color: COLORS.textSecondary }}>No orders yet</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Products Tab */}
      {tab === 'products' && (
        <div data-testid="admin-products">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h3 style={{ fontSize: 18, fontWeight: 700, color: COLORS.textPrimary, fontFamily: 'Outfit, sans-serif' }}>Store Products</h3>
            <button data-testid="add-product-btn" onClick={() => setShowAddProduct(true)}
              style={{ padding: '10px 20px', borderRadius: 10, border: 'none', backgroundColor: COLORS.primary, color: '#FFF', fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>+ Add Product</button>
          </div>
          <div style={{ marginBottom: 24 }}>
            <h4 style={{ fontSize: 14, fontWeight: 600, color: COLORS.textSecondary, marginBottom: 12, textTransform: 'uppercase', letterSpacing: 0.5 }}>Default Products ({products.default_products?.length || 0})</h4>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }}>
              {products.default_products?.map(p => (
                <div key={p.product_id} style={{ backgroundColor: COLORS.surface, borderRadius: 12, padding: 16, border: `1px solid ${COLORS.border}40` }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ fontWeight: 700, color: COLORS.textPrimary, fontSize: 15 }}>{p.name}</span>
                    <span style={{ fontWeight: 800, color: COLORS.primary }}>${p.price}</span>
                  </div>
                  <p style={{ fontSize: 13, color: COLORS.textSecondary, marginTop: 4 }}>{p.description}</p>
                </div>
              ))}
            </div>
          </div>
          {products.custom_products?.length > 0 && (
            <div>
              <h4 style={{ fontSize: 14, fontWeight: 600, color: COLORS.textSecondary, marginBottom: 12, textTransform: 'uppercase', letterSpacing: 0.5 }}>Custom Products ({products.custom_products.length})</h4>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }}>
                {products.custom_products.map(p => (
                  <div key={p.product_id} style={{ backgroundColor: COLORS.surface, borderRadius: 12, padding: 16, border: `1px solid ${COLORS.primary}40` }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontWeight: 700, color: COLORS.textPrimary, fontSize: 15 }}>{p.name}</span>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <span style={{ fontWeight: 800, color: COLORS.primary }}>${p.price}</span>
                        <button onClick={() => handleDeleteProduct(p.product_id)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}><Trash2 size={16} color={COLORS.error} /></button>
                      </div>
                    </div>
                    <p style={{ fontSize: 13, color: COLORS.textSecondary, marginTop: 4 }}>{p.description}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Affiliates Tab */}
      {tab === 'affiliates' && (
        <AffiliatesTab />
      )}

      {/* External API Integrations Tab */}
      {tab === 'integrations' && (
        <IntegrationsTab />
      )}

      {/* Architecture & operations handbook */}
      {tab === 'architecture' && (
        <DocumentationTab />
      )}

      {/* Multi-site Dokploy operator training manual */}
      {tab === 'training' && (
        <TrainingTab />
      )}

      {/* Medical Vault Tab */}
      {tab === 'medical' && (
        <AdminMedicalPage />
      )}

      {/* Group Chat Weekly Report Tab */}
      {tab === 'groupchat' && (
        <GroupChatReportTab />
      )}

      {/* ── Modals ── */}

      {/* Edit User Modal */}
      {editUser && (
        <Modal onClose={() => setEditUser(null)} title="Edit User" testId="edit-user-modal">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div><label style={labelStyle}>Name</label><input value={editUser.name || ''} onChange={e => setEditUser({ ...editUser, name: e.target.value })} style={inputStyle} /></div>
            <div><label style={labelStyle}>Role</label><select value={editUser.role} onChange={e => setEditUser({ ...editUser, role: e.target.value })} style={inputStyle}><option value="user">User</option><option value="admin">Admin</option></select></div>
            <div><label style={labelStyle}>Membership Tier</label><select value={editUser.membership_tier} onChange={e => setEditUser({ ...editUser, membership_tier: parseInt(e.target.value) })} style={inputStyle}>{TIER_NAMES.map((name, i) => <option key={i} value={i}>{name}</option>)}</select></div>
            <button data-testid="save-user-btn" onClick={handleUpdateUser} style={{ padding: '12px', borderRadius: 10, border: 'none', backgroundColor: COLORS.primary, color: '#FFF', fontWeight: 700, fontSize: 15, cursor: 'pointer', marginTop: 8 }}>Save Changes</button>
          </div>
        </Modal>
      )}

      {/* Add Product Modal */}
      {showAddProduct && (
        <Modal onClose={() => setShowAddProduct(false)} title="Add Product" testId="add-product-modal">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <input placeholder="Product Name" value={newProduct.name} onChange={e => setNewProduct({ ...newProduct, name: e.target.value })} style={inputStyle} />
            <textarea placeholder="Description" value={newProduct.description} onChange={e => setNewProduct({ ...newProduct, description: e.target.value })} rows={3} style={{ ...inputStyle, resize: 'vertical' }} />
            <input placeholder="Price" type="number" step="0.01" value={newProduct.price} onChange={e => setNewProduct({ ...newProduct, price: e.target.value })} style={inputStyle} />
            <select value={newProduct.category} onChange={e => setNewProduct({ ...newProduct, category: e.target.value })} style={inputStyle}>
              <option value="health-devices">Health Devices</option><option value="consultations">Consultations</option><option value="seminars">Seminars</option><option value="clothing">Clothing</option><option value="environmental">Environmental</option>
            </select>
            <button data-testid="submit-product-btn" onClick={handleAddProduct} style={{ padding: '12px', borderRadius: 10, border: 'none', backgroundColor: COLORS.primary, color: '#FFF', fontWeight: 700, fontSize: 15, cursor: 'pointer', marginTop: 8 }}>Add Product</button>
          </div>
        </Modal>
      )}

      {/* New Article Modal */}
      {showAddArticle && (
        <Modal onClose={() => setShowAddArticle(false)} title="New Research Article" testId="add-article-modal" wide>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div><label style={labelStyle}>Title</label><input data-testid="article-title-input" placeholder="Article title" value={newArticle.title} onChange={e => setNewArticle({ ...newArticle, title: e.target.value })} style={inputStyle} /></div>
            <div><label style={labelStyle}>Summary</label><textarea data-testid="article-summary-input" placeholder="Brief summary (shown on cards)" value={newArticle.summary} onChange={e => setNewArticle({ ...newArticle, summary: e.target.value })} rows={2} style={{ ...inputStyle, resize: 'vertical' }} /></div>
            <div><label style={labelStyle}>Full Content</label><textarea data-testid="article-content-input" placeholder="Full article text..." value={newArticle.content} onChange={e => setNewArticle({ ...newArticle, content: e.target.value })} rows={8} style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.6 }} /></div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div><label style={labelStyle}>Category</label><select value={newArticle.category} onChange={e => setNewArticle({ ...newArticle, category: e.target.value })} style={inputStyle}>{RESEARCH_CATEGORIES.map(c => <option key={c} value={c}>{c.replace('_', ' ')}</option>)}</select></div>
              <div><label style={labelStyle}>Source</label><input placeholder="e.g. Stanford" value={newArticle.source} onChange={e => setNewArticle({ ...newArticle, source: e.target.value })} style={inputStyle} /></div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div><label style={labelStyle}>Read Time</label><input placeholder="e.g. 8 min" value={newArticle.read_time} onChange={e => setNewArticle({ ...newArticle, read_time: e.target.value })} style={inputStyle} /></div>
              <div><label style={labelStyle}>Min Tier Required</label><select value={newArticle.tier_required} onChange={e => setNewArticle({ ...newArticle, tier_required: parseInt(e.target.value) })} style={inputStyle}>{TIER_NAMES.map((name, i) => <option key={i} value={i}>{name} (Tier {i})</option>)}</select></div>
            </div>
            <button data-testid="submit-article-btn" onClick={handleAddArticle} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '12px', borderRadius: 10, border: 'none', backgroundColor: COLORS.primary, color: '#FFF', fontWeight: 700, fontSize: 15, cursor: 'pointer', marginTop: 8 }}><Save size={18} /> Save Article</button>
          </div>
        </Modal>
      )}

      {/* Edit Article Modal */}
      {editArticle && (
        <Modal onClose={() => setEditArticle(null)} title="Edit Research Article" testId="edit-article-modal" wide>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div><label style={labelStyle}>Title</label><input value={editArticle.title} onChange={e => setEditArticle({ ...editArticle, title: e.target.value })} style={inputStyle} /></div>
            <div><label style={labelStyle}>Summary</label><textarea value={editArticle.summary} onChange={e => setEditArticle({ ...editArticle, summary: e.target.value })} rows={2} style={{ ...inputStyle, resize: 'vertical' }} /></div>
            <div><label style={labelStyle}>Full Content</label><textarea value={editArticle.content || ''} onChange={e => setEditArticle({ ...editArticle, content: e.target.value })} rows={8} style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.6 }} /></div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div><label style={labelStyle}>Category</label><select value={editArticle.category} onChange={e => setEditArticle({ ...editArticle, category: e.target.value })} style={inputStyle}>{RESEARCH_CATEGORIES.map(c => <option key={c} value={c}>{c.replace('_', ' ')}</option>)}</select></div>
              <div><label style={labelStyle}>Source</label><input value={editArticle.source || ''} onChange={e => setEditArticle({ ...editArticle, source: e.target.value })} style={inputStyle} /></div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div><label style={labelStyle}>Read Time</label><input value={editArticle.read_time || ''} onChange={e => setEditArticle({ ...editArticle, read_time: e.target.value })} style={inputStyle} /></div>
              <div><label style={labelStyle}>Min Tier</label><select value={editArticle.tier_required} onChange={e => setEditArticle({ ...editArticle, tier_required: parseInt(e.target.value) })} style={inputStyle}>{TIER_NAMES.map((name, i) => <option key={i} value={i}>{name}</option>)}</select></div>
            </div>
            <button data-testid="save-article-btn" onClick={handleUpdateArticle} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '12px', borderRadius: 10, border: 'none', backgroundColor: COLORS.primary, color: '#FFF', fontWeight: 700, fontSize: 15, cursor: 'pointer', marginTop: 8 }}><Save size={18} /> Update Article</button>
          </div>
        </Modal>
      )}

      {/* Import Text Modal */}
      {showImport && (
        <Modal onClose={() => setShowImport(false)} title="Import Research from Text" testId="import-research-modal" wide>
          <p style={{ fontSize: 13, color: COLORS.textSecondary, marginBottom: 16, lineHeight: 1.5 }}>
            Paste your research text below. The <strong>first line</strong> becomes the article title. Everything else becomes the content. A summary and read time will be auto-generated.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div>
              <label style={labelStyle}>Paste Research Text</label>
              <textarea data-testid="import-text-input" placeholder={"Article Title Goes Here\n\nFull article content goes here. Paste the entire research text, study summary, or article body.\n\nMultiple paragraphs are supported..."} value={importData.text} onChange={e => setImportData({ ...importData, text: e.target.value })} rows={12} style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.6 }} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
              <div><label style={labelStyle}>Category</label><select value={importData.category} onChange={e => setImportData({ ...importData, category: e.target.value })} style={inputStyle}>{RESEARCH_CATEGORIES.map(c => <option key={c} value={c}>{c.replace('_', ' ')}</option>)}</select></div>
              <div><label style={labelStyle}>Source</label><input placeholder="e.g. NIA Research" value={importData.source} onChange={e => setImportData({ ...importData, source: e.target.value })} style={inputStyle} /></div>
              <div><label style={labelStyle}>Min Tier</label><select value={importData.tier_required} onChange={e => setImportData({ ...importData, tier_required: parseInt(e.target.value) })} style={inputStyle}>{TIER_NAMES.map((name, i) => <option key={i} value={i}>{name}</option>)}</select></div>
            </div>
            {importData.text.trim() && (
              <div style={{ backgroundColor: COLORS.surfaceSecondary, borderRadius: 10, padding: 14 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: COLORS.textSecondary, marginBottom: 4 }}>Preview</div>
                <div style={{ fontSize: 15, fontWeight: 700, color: COLORS.textPrimary }}>{importData.text.split('\n')[0]?.trim().replace(/^#+\s*/, '') || 'Untitled'}</div>
                <div style={{ fontSize: 12, color: COLORS.textSecondary, marginTop: 4 }}>{importData.text.split(/\s+/).length} words ~ {Math.max(1, Math.round(importData.text.split(/\s+/).length / 200))} min read</div>
              </div>
            )}
            <button data-testid="submit-import-btn" onClick={handleImport} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '12px', borderRadius: 10, border: 'none', backgroundColor: COLORS.success, color: '#FFF', fontWeight: 700, fontSize: 15, cursor: 'pointer', marginTop: 8 }}><Upload size={18} /> Import Article</button>
          </div>
        </Modal>
      )}

      {/* Upload File Modal */}
      {showUpload && (
        <Modal onClose={resetUploadModal} title={`Upload Publications${uploadFiles.length > 0 ? ` (${uploadFiles.length} file${uploadFiles.length > 1 ? 's' : ''})` : ''}`} testId="upload-file-modal" wide>
          {uploadResults.length > 0 ? (
            <div data-testid="upload-results">
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                <div style={{ width: 48, height: 48, borderRadius: 24, backgroundColor: `${COLORS.success}15`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <CheckCircle2 size={24} color={COLORS.success} />
                </div>
                <div>
                  <div style={{ fontSize: 18, fontWeight: 700, color: COLORS.textPrimary, fontFamily: 'Outfit, sans-serif' }}>Import Complete</div>
                  <div style={{ fontSize: 13, color: COLORS.textSecondary }}>{uploadResults.filter(r => r.status === 'success').length} of {uploadResults.length} files imported successfully</div>
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 320, overflowY: 'auto', marginBottom: 16 }}>
                {uploadResults.map((r, i) => (
                  <div key={i} data-testid={`upload-result-${i}`} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderRadius: 10, backgroundColor: r.status === 'success' ? `${COLORS.success}08` : '#FEE2E2', border: `1px solid ${r.status === 'success' ? COLORS.success + '20' : '#FECACA'}` }}>
                    {r.status === 'success' ? <CheckCircle2 size={18} color={COLORS.success} /> : <AlertCircle size={18} color="#EF4444" />}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: COLORS.textPrimary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {r.status === 'success' ? r.data?.title : r.file}
                      </div>
                      <div style={{ fontSize: 12, color: r.status === 'success' ? COLORS.textSecondary : '#EF4444' }}>
                        {r.status === 'success' ? `${r.data?.read_time} · ${r.file}` : r.error}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div style={{ display: 'flex', gap: 12 }}>
                <button onClick={() => { setUploadResults([]); setUploadFiles([]); }} style={{ flex: 1, padding: '12px', borderRadius: 10, border: `1px solid ${COLORS.border}`, backgroundColor: '#FFF', color: COLORS.textSecondary, fontWeight: 600, fontSize: 14, cursor: 'pointer' }}>Upload More</button>
                <button onClick={resetUploadModal} style={{ flex: 1, padding: '12px', borderRadius: 10, border: 'none', backgroundColor: COLORS.primary, color: '#FFF', fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>Done</button>
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <p style={{ fontSize: 13, color: COLORS.textSecondary, lineHeight: 1.5, margin: 0 }}>
                Select one or more files from your computer. Supported: <strong>.txt, .md, .pdf, .docx</strong>
              </p>

              {/* Drop zone */}
              <div
                data-testid="file-drop-zone"
                onClick={() => document.getElementById('research-file-input')?.click()}
                style={{
                  border: `2px dashed ${uploadFiles.length > 0 ? COLORS.success : COLORS.border}`,
                  borderRadius: 16, padding: uploadFiles.length > 0 ? '16px 20px' : '36px 24px',
                  textAlign: 'center', cursor: 'pointer',
                  backgroundColor: uploadFiles.length > 0 ? `${COLORS.success}06` : COLORS.surfaceSecondary,
                  transition: 'all 0.2s',
                }}
              >
                <input
                  id="research-file-input"
                  data-testid="file-input"
                  type="file"
                  accept=".txt,.md,.pdf,.docx,.doc"
                  multiple
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                />
                {uploadFiles.length > 0 ? (
                  <div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 10 }}>
                      {uploadFiles.map((f, i) => (
                        <div key={i} data-testid={`queued-file-${i}`} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', backgroundColor: '#FFF', borderRadius: 10, border: `1px solid ${COLORS.border}40` }}>
                          <FileText size={18} color={COLORS.success} />
                          <div style={{ flex: 1, textAlign: 'left' }}>
                            <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.textPrimary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</div>
                            <div style={{ fontSize: 11, color: COLORS.textSecondary }}>{(f.size / 1024).toFixed(1)} KB</div>
                          </div>
                          <button onClick={(e) => { e.stopPropagation(); removeFile(i); }} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2 }}><X size={16} color={COLORS.textSecondary} /></button>
                        </div>
                      ))}
                    </div>
                    <div style={{ fontSize: 13, color: COLORS.primary, fontWeight: 600 }}>+ Click to add more files</div>
                  </div>
                ) : (
                  <>
                    <div style={{ width: 56, height: 56, borderRadius: 16, backgroundColor: `${COLORS.primary}12`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
                      <Upload size={28} color={COLORS.primary} />
                    </div>
                    <div style={{ fontSize: 15, fontWeight: 700, color: COLORS.textPrimary, marginBottom: 4 }}>Click to select files</div>
                    <div style={{ fontSize: 13, color: COLORS.textSecondary }}>TXT, MD, PDF, or DOCX — select multiple files at once</div>
                  </>
                )}
              </div>

              {/* Metadata */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                <div>
                  <label style={labelStyle}>Category</label>
                  <select value={uploadMeta.category} onChange={e => setUploadMeta({ ...uploadMeta, category: e.target.value })} style={inputStyle}>
                    {RESEARCH_CATEGORIES.map(c => <option key={c} value={c}>{c.replace('_', ' ')}</option>)}
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>Source</label>
                  <input placeholder="e.g. NIA Research" value={uploadMeta.source} onChange={e => setUploadMeta({ ...uploadMeta, source: e.target.value })} style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>Min Tier</label>
                  <select value={uploadMeta.tier_required} onChange={e => setUploadMeta({ ...uploadMeta, tier_required: parseInt(e.target.value) })} style={inputStyle}>
                    {TIER_NAMES.map((name, i) => <option key={i} value={i}>{name}</option>)}
                  </select>
                </div>
              </div>

              {/* Progress */}
              {uploading && (
                <div style={{ backgroundColor: COLORS.surfaceSecondary, borderRadius: 10, padding: 14 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 6 }}>
                    <span style={{ color: COLORS.textSecondary }}>Uploading...</span>
                    <span style={{ fontWeight: 700, color: COLORS.primary }}>{uploadProgress} / {uploadFiles.length}</span>
                  </div>
                  <div style={{ height: 6, backgroundColor: COLORS.border, borderRadius: 3, overflow: 'hidden' }}>
                    <div style={{ height: 6, width: `${(uploadProgress / uploadFiles.length) * 100}%`, backgroundColor: COLORS.primary, borderRadius: 3, transition: 'width 0.3s' }} />
                  </div>
                </div>
              )}

              <button
                data-testid="submit-upload-btn"
                onClick={handleFileUpload}
                disabled={uploadFiles.length === 0 || uploading}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  padding: '14px', borderRadius: 10, border: 'none',
                  backgroundColor: uploadFiles.length > 0 ? COLORS.success : COLORS.border,
                  color: '#FFF', fontWeight: 700, fontSize: 15,
                  cursor: uploadFiles.length > 0 && !uploading ? 'pointer' : 'not-allowed',
                  opacity: uploading ? 0.7 : 1,
                }}
              >
                {uploading
                  ? <><Loader2 size={18} /> Processing {uploadProgress} of {uploadFiles.length}...</>
                  : <><Upload size={18} /> Upload {uploadFiles.length > 0 ? `${uploadFiles.length} File${uploadFiles.length > 1 ? 's' : ''}` : '& Import'}</>
                }
              </button>
            </div>
          )}
        </Modal>
      )}

      {/* Scrape Web Modal */}
      {showScrape && (
        <Modal onClose={() => { setShowScrape(false); setScrapeResult(null); setScrapeUrl(''); }} title="Scrape Article from Web" testId="scrape-modal" wide>
          {scrapeResult ? (
            <div data-testid="scrape-success" style={{ textAlign: 'center', padding: '16px 0' }}>
              <div style={{ width: 56, height: 56, borderRadius: 28, backgroundColor: `${COLORS.success}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px' }}><CheckCircle2 size={28} color={COLORS.success} /></div>
              <h4 style={{ fontSize: 18, fontWeight: 700, color: COLORS.textPrimary, fontFamily: 'Outfit, sans-serif', marginBottom: 6 }}>Article Scraped!</h4>
              <p style={{ fontSize: 15, fontWeight: 600, color: COLORS.textPrimary }}>{scrapeResult.title}</p>
              <p style={{ fontSize: 13, color: COLORS.textSecondary }}>{scrapeResult.source} · {scrapeResult.read_time} · {scrapeResult.content?.length || 0} chars</p>
              <div style={{ display: 'flex', gap: 12, marginTop: 20 }}>
                <button onClick={() => { setScrapeResult(null); setScrapeUrl(''); }} style={{ flex: 1, padding: '12px', borderRadius: 10, border: `1px solid ${COLORS.border}`, backgroundColor: '#FFF', color: COLORS.textSecondary, fontWeight: 600, cursor: 'pointer' }}>Scrape Another</button>
                <button onClick={() => { setShowScrape(false); setScrapeResult(null); setScrapeUrl(''); }} style={{ flex: 1, padding: '12px', borderRadius: 10, border: 'none', backgroundColor: COLORS.primary, color: '#FFF', fontWeight: 700, cursor: 'pointer' }}>Done</button>
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <p style={{ fontSize: 13, color: COLORS.textSecondary, lineHeight: 1.5, margin: 0 }}>Enter a URL to scrape and import as a research article. Works best with health research sites (NIA, PubMed, Stanford, etc.).</p>
              <div>
                <label style={labelStyle}>Article URL</label>
                <input data-testid="scrape-url" placeholder="https://www.nia.nih.gov/news/..." value={scrapeUrl} onChange={e => setScrapeUrl(e.target.value)} style={inputStyle} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div><label style={labelStyle}>Category</label><select value={scrapeMeta.category} onChange={e => setScrapeMeta({ ...scrapeMeta, category: e.target.value })} style={inputStyle}>{RESEARCH_CATEGORIES.map(c => <option key={c} value={c}>{c.replace('_', ' ')}</option>)}</select></div>
                <div><label style={labelStyle}>Min Tier</label><select value={scrapeMeta.tier_required} onChange={e => setScrapeMeta({ ...scrapeMeta, tier_required: parseInt(e.target.value) })} style={inputStyle}>{TIER_NAMES.map((name, i) => <option key={i} value={i}>{name}</option>)}</select></div>
              </div>
              <button data-testid="submit-scrape-btn" onClick={handleScrape} disabled={!scrapeUrl.trim() || scraping}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '14px', borderRadius: 10, border: 'none', backgroundColor: scrapeUrl.trim() ? COLORS.secondary : COLORS.border, color: '#FFF', fontWeight: 700, fontSize: 15, cursor: scrapeUrl.trim() && !scraping ? 'pointer' : 'not-allowed', opacity: scraping ? 0.7 : 1 }}>
                {scraping ? <><Loader2 size={18} /> Scraping...</> : <><Globe size={18} /> Scrape &amp; Import</>}
              </button>
            </div>
          )}
        </Modal>
      )}

      {/* View Article Modal */}
      {viewArticle && (
        <Modal onClose={() => setViewArticle(null)} title={viewArticle.title} testId="view-article-modal" wide>
          <div style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
              <span style={{ padding: '4px 10px', borderRadius: 8, fontSize: 12, fontWeight: 700, backgroundColor: `${COLORS.primary}15`, color: COLORS.primary, textTransform: 'capitalize' }}>{viewArticle.category}</span>
              {viewArticle.source && <span style={{ fontSize: 13, color: COLORS.textSecondary }}>{viewArticle.source}</span>}
              <span style={{ fontSize: 13, color: COLORS.textSecondary }}>{viewArticle.read_time}</span>
              <span style={{ fontSize: 13, color: COLORS.textSecondary }}>{viewArticle.published}</span>
            </div>
            {viewArticle.summary && (
              <div style={{ backgroundColor: `${COLORS.primary}08`, borderLeft: `3px solid ${COLORS.primary}`, borderRadius: '0 8px 8px 0', padding: '12px 16px', marginBottom: 16 }}>
                <p style={{ fontSize: 14, color: COLORS.textPrimary, lineHeight: 1.6, margin: 0, fontWeight: 500 }}>{viewArticle.summary}</p>
              </div>
            )}
            {viewArticle.content ? (
              <div style={{ fontSize: 14, color: COLORS.textPrimary, lineHeight: 1.7, whiteSpace: 'pre-line', maxHeight: 400, overflowY: 'auto', padding: '4px 0' }}>
                {viewArticle.content}
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: 24, color: COLORS.textSecondary }}>
                <BookOpen size={32} color={COLORS.border} />
                <p style={{ marginTop: 8 }}>No full content available. Click Edit to add content.</p>
              </div>
            )}
          </div>
          <div style={{ display: 'flex', gap: 12 }}>
            <button onClick={() => { setViewArticle(null); setEditArticle({ ...viewArticle }); }} style={{ flex: 1, padding: '12px', borderRadius: 10, border: `1px solid ${COLORS.primary}`, backgroundColor: 'transparent', color: COLORS.primary, fontWeight: 600, fontSize: 14, cursor: 'pointer' }}>Edit Article</button>
            <button onClick={() => setViewArticle(null)} style={{ flex: 1, padding: '12px', borderRadius: 10, border: 'none', backgroundColor: COLORS.primary, color: '#FFF', fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>Close</button>
          </div>
        </Modal>
      )}

      {/* Send Blast Modal */}
      {showBlastForm && (
        <Modal onClose={() => setShowBlastForm(false)} title="Send Content Blast" testId="blast-modal" wide>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <p style={{ fontSize: 13, color: COLORS.textSecondary, lineHeight: 1.5, margin: 0 }}>
              Send curated health content (VILPA, Epigenetics, Cold Exposure) to tier 2+ users. <strong>Tier 3 (Elite)</strong> users will also receive full article content and links to your latest research.
            </p>
            <div>
              <label style={labelStyle}>Email Subject (optional)</label>
              <input data-testid="blast-subject" placeholder="e.g. Your Weekly Health Insights" value={blastForm.subject} onChange={e => setBlastForm({ ...blastForm, subject: e.target.value })} style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Custom Message (optional)</label>
              <textarea data-testid="blast-message" placeholder="Add a personal note to the email..." value={blastForm.custom_message} onChange={e => setBlastForm({ ...blastForm, custom_message: e.target.value })} rows={3} style={{ ...inputStyle, resize: 'vertical' }} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <label style={labelStyle}>Minimum Tier</label>
                <select data-testid="blast-tier" value={blastForm.min_tier} onChange={e => setBlastForm({ ...blastForm, min_tier: parseInt(e.target.value) })} style={inputStyle}>
                  {TIER_NAMES.map((name, i) => i >= 2 ? <option key={i} value={i}>{name} (Tier {i}+)</option> : null)}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Include Article Links</label>
                <select value={blastForm.include_article_links ? 'yes' : 'no'} onChange={e => setBlastForm({ ...blastForm, include_article_links: e.target.value === 'yes' })} style={inputStyle}>
                  <option value="yes">Yes — for Tier 3+</option>
                  <option value="no">No</option>
                </select>
              </div>
            </div>
            <div style={{ backgroundColor: COLORS.surfaceSecondary, borderRadius: 10, padding: 14 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: COLORS.textSecondary, marginBottom: 6 }}>Content includes</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <div style={{ fontSize: 13, color: COLORS.textPrimary }}>VILPA: Vigorous Intermittent Lifestyle Physical Activity</div>
                <div style={{ fontSize: 13, color: COLORS.textPrimary }}>Epigenetics & Activities of Daily Living</div>
                <div style={{ fontSize: 13, color: COLORS.textPrimary }}>Cold Exposure & Immune Response</div>
              </div>
            </div>
            <button data-testid="send-blast-btn" onClick={handleSendBlast} disabled={blasting}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '14px', borderRadius: 10, border: 'none', backgroundColor: COLORS.primary, color: '#FFF', fontWeight: 700, fontSize: 15, cursor: 'pointer', opacity: blasting ? 0.7 : 1 }}>
              {blasting ? <><Loader2 size={18} /> Sending...</> : <><Send size={18} /> Send to Tier {blastForm.min_tier}+ Users</>}
            </button>
          </div>
        </Modal>
      )}
    </div>

  );
}
